"use client";
import { useEffect, useMemo, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { useSearchProductsQuery } from "@/api/AppApi/services/product";
import type { IProductsSearchParams } from "@/api/AppApi/services/product/interfaces/api-product-interfaces";
import { Link } from "@/app/components/lib/navigation/Link";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { Spinner } from "@/utils/bootstrap";
import { getFbcCookie, getFbpCookie } from "@/utils/functions/cookieOperations";
import useEventTrackers from "@/utils/helpers/pixel-helpers/hooks/useEventTrackers/useEventTrackers";
import SearchResultItem from "../search-helpers/SearchResultItem";

interface Props {
  id: string;
  term: string;
  category: string;
  onSelect: () => void;
}

// Live product suggestions under the advanced search bar, scoped to the selected
// category. Same endpoint, tracking and result rows as the button layout's dropdown.
const AdvancedSearchSuggestions: React.FC<Props> = ({
  id,
  term,
  category,
  onSelect,
}) => {
  const t = useTranslation();
  const { GTagSearchEventTracher } = useEventTrackers();
  // One event_id per (term, category) so the browser Search event and the
  // backend CAPI event dedupe against each other. The deps are the trigger, not
  // inputs, hence the disable.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const trackingId = useMemo(() => uuidv4(), [term, category]);
  const lastTrackedId = useRef<string | null>(null);

  const params: IProductsSearchParams = {
    q: term,
    ...(category && { category_id: category }),
    event_id: trackingId,
    fbc: (getFbcCookie() as string) ?? null,
    fbp: (getFbpCookie() as string) ?? null,
  };
  const { data, isLoading, isError } = useSearchProductsQuery({ params });
  const products = data?.result;

  useEffect(() => {
    if (!products || lastTrackedId.current === trackingId) return;
    lastTrackedId.current = trackingId;
    GTagSearchEventTracher(term, !!products.length, trackingId);
  }, [products, trackingId, term, GTagSearchEventTracher]);

  if (isError) return null;

  return (
    <div id={id} className="advanced-search__results">
      {isLoading ? (
        <div className="advanced-search__status">
          <Spinner animation="border" size="sm" />
        </div>
      ) : !products?.length ? (
        <p className="advanced-search__status">{t.no_results}</p>
      ) : (
        <ul className="advanced-search__list" aria-label={t.search}>
          {products.map((product) => (
            <li key={product.id}>
              <Link
                prefetch={false}
                href={"/products/" + product.slug}
                skipCheckingForLang
                className="advanced-search__result"
                onClick={onSelect}
              >
                <SearchResultItem product={product} searchTerm={term} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdvancedSearchSuggestions;
